import React, { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { Button } from '../../components/styled/Button/Button'
import { updateRealEstateSubtype } from '../../store/lead/leadSlice'
import { RealEstateType } from '../../@types/realEstateType'
import { ApartmentSubtype, HouseSubtype } from '../../@types/realEstateSubtype'
import { enrichLead } from '../../store/lead/thunks'
import { EnrichLeadValuationType } from '../../@types/enrichLeadValuationtype'
import { reportTrackingEvent } from '../../tracking/thunks'
import { useReportViewEvent } from '../../hooks'
import { RealEstateSubTypeIcon } from '../../components/icons/StepIcons/RealEstateSubTypeIcon'
import { IntegrationType } from '../../@types/config'
import { richValuationFlowProgress } from '../../progress/valuationProgressHelper'
import { STEP } from '../../@types/state'
import { ReduxState } from '../../store/reducers'

interface RealEstateSubtypeProps {
  goToNextStep: () => unknown
  goToPreviousStep: () => unknown
  goToErrorStep: () => unknown
}

const getRealEstateSubtypeGermanString = (realEstateType?: RealEstateType) => {
  switch (realEstateType) {
    case RealEstateType.HOUSE:
      return {
        [HouseSubtype.DETACHED_HOUSE]: 'Einfamilienhaus',
        [HouseSubtype.SEMI_DETACHED_HOUSE]: 'Doppelhaushälfte',
        [HouseSubtype.TERRACED_HOUSE]: 'Reihenhaus',
        [HouseSubtype.MULTI_FAMILY_HOUSE]: 'Mehrfamilienhaus',
      }
    case RealEstateType.APARTMENT:
      return {
        [ApartmentSubtype.BASEMENT]: 'Souterrain',
        [ApartmentSubtype.ATTIC]: 'Dachgeschoss',
        [ApartmentSubtype.GROUND_FLOOR]: 'Erdgeschoss',
        [ApartmentSubtype.DUPLEX]: 'Maisonette',
        [ApartmentSubtype.APARTMENT]: 'Etagenwohnung',
        [ApartmentSubtype.LOFT]: 'Loft',
      }
    default:
      return {
        ['DEFAULT']: '',
      }
  }
}

const getHeadline = (realEstateType?: RealEstateType) => {
  switch (realEstateType) {
    case RealEstateType.HOUSE:
      return 'Um welchen Haustyp handelt es sich?'
    case RealEstateType.APARTMENT:
      return 'Um welchen Wohnungstyp handelt es sich?'
    default:
      return ''
  }
}

export const RealEstateSubtypeStep: FC<RealEstateSubtypeProps> = (props) => {
  const valuationBarProgress = richValuationFlowProgress(STEP.REAL_ESTATE_SUBTYPE)
  const {
    config: { theme, integrationType },
    lead: { id: leadId, realEstateType },
  } = useSelector((state: ReduxState) => state)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  const dispatch = useDispatch()

  const goToNextStep = (realEstateSubtype: ApartmentSubtype | HouseSubtype) => {
    dispatch(updateRealEstateSubtype(realEstateSubtype))
    if (!leadId) {
      console.error('Lead id prop does not exist')
      props.goToErrorStep()
      return
    }
    dispatch(
      enrichLead({
        enrichValuationType: EnrichLeadValuationType.SINGLE_VALUATION,
        valuationForStep: STEP.FURNISHING_TYPE,
        pageTitle: 'propertysubtype',
        request: {
          id: leadId,
          buildingType: realEstateSubtype,
        },
      })
    )

    dispatch(
      reportTrackingEvent({
        pageTitle: 'propertysubtype',
        label: `propertysubtype_submit`,
        inputValue: realEstateSubtype,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: realEstateSubtype,
        },
      })
    )

    props.goToNextStep()
  }

  useReportViewEvent({ pageTitle: 'propertysubtype' })

  return (
    <StepTemplate
      goToPreviousStep={props.goToPreviousStep}
      icon={<RealEstateSubTypeIcon />}
      headline={getHeadline(realEstateType)}
      displayNext={false}
      displayBack={true}
      valuationBarProgress={valuationBarProgress}
      backgroundColor={theme.background}
      textColor={theme.text}
      secondaryLight={theme.secondaryLight}
    >
      <div data-testid='real-estate-subtype-container'>
        {Object.entries(getRealEstateSubtypeGermanString(realEstateType)).map(([key, label]) => (
          <Button
            key={key}
            text={label as string}
            onClick={() => goToNextStep(key as ApartmentSubtype | HouseSubtype)}
            className='margin-right-m margin-bottom-xl'
            color={isIv24Integration ? '#FFFFFF' : theme.text}
            borderColor={theme?.secondary}
            backgroundColor={`${
              isIv24Integration ? theme?.secondary : theme.background !== `` ? theme.background : null
            }`}
          />
        ))}
      </div>
    </StepTemplate>
  )
}

export default RealEstateSubtypeStep
